import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { useDeepCompareMemo } from "@/hooks/use-deep-compare-memo";
import { Music, Calendar, Building, ChevronDown, HelpCircle, BarChart3, Smartphone, RefreshCw, Plus, CheckCircle, Grid3x3, List, Search, Filter, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { HelpModal } from "@/components/help-modal";
import { PWAInstallModal } from "@/components/pwa-install-modal";
import { useLocation } from "wouter";
import CreateEventModal from "@/components/create-event-modal";
import { AppHeader } from "@/components/app-header";
import { PageBackground } from "@/components/ui/page-background";
import { useAuth } from "@/hooks/use-auth";
import { usePWADetection } from "@/hooks/use-pwa-detection";
import { usePWANavigationGuard } from "@/hooks/use-pwa-navigation-guard";
import { EventCard } from "@/components/event-card";
import { EventActionsDropdown } from "@/components/event-actions-dropdown";
import { Squircle } from "@/components/ui/squircle";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import type { EventWithOrganisationAndPerformerCount, Organisation } from "@shared/schema";

async function fetchEvents(): Promise<EventWithOrganisationAndPerformerCount[]> {
  const response = await fetch('/api/events');
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('auth-error'));
    }
    throw new Error('Failed to fetch events');
  }
  return response.json();
}

async function fetchOrganisations(): Promise<Organisation[]> {
  const response = await fetch('/api/organisations');
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('auth-error'));
    }
    throw new Error('Failed to fetch organisations');
  }
  return response.json();
}

type GroupedEvents = Record<string, { org: Organisation, upcomingEvents: EventWithOrganisationAndPerformerCount[], pastEvents: EventWithOrganisationAndPerformerCount[] }>;

export default function EventManagement() {
  const [, setLocation] = useLocation();
  const [showCreateModal, setShowCreateModal] = useState<false | number>(false);
  const [preventNavigation] = useState<Record<number, boolean>>({});
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showPWAInstallModal, setShowPWAInstallModal] = useState(false);

  // Prevent back navigation in PWA to avoid going back to login page
  usePWANavigationGuard({ preventBack: true });
  const [expandedPastEvents, setExpandedPastEvents] = useState<Record<string, boolean>>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [showOnlyUpcoming, setShowOnlyUpcoming] = useState(true);
  const { user, isLoading: isAuthLoading } = useAuth();
  const { isPWA, shouldShowInstallButton } = usePWADetection();
  
  // Mobile detection for PWA install button
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  // Service worker registration (without automatic reload)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // Register the service worker but don't auto-update
        // User can manually update with the Force Update button
      });
    }
  }, []);

  const { data: events = [], isLoading: isLoadingEvents } = useQuery<EventWithOrganisationAndPerformerCount[]>({
    queryKey: ["events"],
    queryFn: fetchEvents,
  });

  const { data: organisations = [], isLoading: isLoadingOrganisations } = useQuery<Organisation[]>({
    queryKey: ["organisations"],
    queryFn: fetchOrganisations,
    enabled: !isAuthLoading && user?.role === 'god',
  });


  const handleSelectEvent = (eventId: number) => {
    // Prevent navigation if a dropdown action is in progress
    if (preventNavigation[eventId]) {
      return;
    }
    setLocation(`/event/${eventId}`);
  };


  const groupedEvents = useDeepCompareMemo(() => {
    const groups: GroupedEvents = {};
    let orgs: (Organisation | undefined)[] = [];

    if (user?.role === 'god') {
      orgs = organisations;
    } else {
      orgs = user?.organisationUsers?.map(ou => ou.organisation) ?? [];
    }

    orgs.forEach((organisation) => {
      if (organisation) {
        const orgName = organisation.name || 'Uncategorised Events';
        if (!groups[orgName]) {
          groups[orgName] = { org: organisation, upcomingEvents: [], pastEvents: [] };
        }
      }
    });

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Start of today

    events?.forEach((events) => {
      if (events.organisation) {
        const orgName = events.organisation.name || 'Uncategorised Events';
        if (groups[orgName]) {
          const isPast = new Date(events.date) < currentDate;
          if (isPast) {
            groups[orgName].pastEvents.push(events);
          } else {
            groups[orgName].upcomingEvents.push(events);
          }
        }
      }
    });

    // Sort events within each organization
    Object.keys(groups).forEach(orgName => {
      groups[orgName].pastEvents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      groups[orgName].upcomingEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    // Filter events based on search terms
    Object.keys(groups).forEach(orgName => {
      const searchTerm = searchTerms[orgName]?.toLowerCase() || '';
      if (searchTerm) {
        groups[orgName].upcomingEvents = groups[orgName].upcomingEvents.filter(event =>
          event.name.toLowerCase().includes(searchTerm)
        );
        groups[orgName].pastEvents = groups[orgName].pastEvents.filter(event =>
          event.name.toLowerCase().includes(searchTerm)
        );
      }
    });

    return groups;
  }, [events, user, organisations, searchTerms]);

  // Get all events for the selected organization
  const filteredEvents = useMemo(() => {
    let allEvents: EventWithOrganisationAndPerformerCount[] = [];
    
    if (selectedOrganization === 'all') {
      Object.values(groupedEvents).forEach(({ upcomingEvents, pastEvents }) => {
        allEvents = [...allEvents, ...upcomingEvents, ...pastEvents];
      });
    } else {
      const orgData = groupedEvents[selectedOrganization];
      if (orgData) {
        allEvents = [...orgData.upcomingEvents, ...orgData.pastEvents];
      }
    }

    // Apply global search filter
    if (globalSearchTerm) {
      allEvents = allEvents.filter(event => 
        event.name.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        event.venue?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        event.description?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      );
    }

    // Apply upcoming/past filter
    if (showOnlyUpcoming) {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      allEvents = allEvents.filter(event => new Date(event.date) >= currentDate);
    }

    // Sort by date
    allEvents.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return showOnlyUpcoming ? dateA - dateB : dateB - dateA;
    });

    return allEvents;
  }, [groupedEvents, selectedOrganization, globalSearchTerm, showOnlyUpcoming]);

  const userIsOwnerOfOrg = (organisationId: number | undefined) => {
    if (!organisationId) return false;
    if (user?.role === 'god') return true;
    
    const isOwner = user?.organisationUsers?.some((ou) => {
      return ou.organisation.id === organisationId && ou.role === 'owner';
    });
    
    return isOwner || false;
  }

  const handleCreateEvent = (orgId: number) => {
    if (!orgId) {
      return;
    }
    setShowCreateModal(orgId);
  };

  const togglePastEvents = (orgName: string) => {
    setExpandedPastEvents(prev => ({
      ...prev,
      [orgName]: !prev[orgName],
    }));
  };

  const handleSearchChange = (orgName: string, value: string) => {
    setSearchTerms(prev => ({
      ...prev,
      [orgName]: value,
    }));
  };

  const handleForceRefresh = () => {
    // Show animation immediately
    setIsRefreshing(true);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        // Force check for updates
        registration.update().then(() => {
          // If no update found, still refresh to clear cache
          window.location.reload();
        });
      });
    } else {
      // Fallback for browsers without service worker support
      window.location.reload();
    }
  };

  

  const isLoading = isAuthLoading || isLoadingEvents || (user?.role === 'god' && isLoadingOrganisations);

  // Show refreshing animation during browser reload
  if (isRefreshing) {
    return (
      <PageBackground>
        {user?.role === 'god' ? (
          <AppHeader />
        ) : (
          <AppHeader username={user?.username} />
        )}
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <div className="relative mb-4">
              <div className="w-12 h-12 mx-auto border-3 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-1 drop-shadow-sm">
              Refreshing...
            </h2>
            <p className="text-slate-600 drop-shadow-sm text-sm">
              Getting the latest updates
            </p>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (isLoading) {
    return (
      <PageBackground>
        {user?.role === 'god' ? (
          <AppHeader />
        ) : (
          <AppHeader username={user?.username} />
        )}
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
            <p className="text-slate-700 drop-shadow-sm">Loading your events...</p>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      {user?.role === 'god' ? (
        <AppHeader />
      ) : (
        <AppHeader username={user?.username} />
      )}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative z-10">
        {/* Control Panel */}
        <Card className="mb-6 bg-white/30 backdrop-blur-xl border-white/20 shadow-2xl mt-4"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Top row - Organization selector and search */}
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 flex flex-col sm:flex-row gap-2">
                  <Select value={selectedOrganization} onValueChange={setSelectedOrganization}>
                    <SelectTrigger className="w-full sm:w-[250px] bg-white/50 backdrop-blur-sm border-white/30">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent className="bg-white/95 backdrop-blur-xl border-white/30">
                      <SelectItem value="all">All Organizations</SelectItem>
                      {Object.entries(groupedEvents).map(([orgName, { org }]) => (
                        <SelectItem key={orgName} value={orgName}>
                          <div className="flex items-center gap-2">
                            {org?.logoUrl ? (
                              <Squircle 
                                src={org.logoUrl} 
                                alt={`${orgName} Logo`} 
                                size={16}
                                className="flex-shrink-0"
                              />
                            ) : (
                              <Building className="h-4 w-4 text-slate-600" />
                            )}
                            <span>{orgName}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      type="text"
                      placeholder="Search all events..."
                      className="pl-10 bg-white/50 backdrop-blur-sm border-white/30"
                      value={globalSearchTerm}
                      onChange={(e) => setGlobalSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  {selectedOrganization !== 'all' && groupedEvents[selectedOrganization] && userIsOwnerOfOrg(groupedEvents[selectedOrganization].org?.id) && (
                    <>
                      <Button
                        onClick={() => handleCreateEvent(groupedEvents[selectedOrganization].org.id)}
                        className="bg-primary hover:bg-blue-600 text-white"
                        size="default"
                      >
                        <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="sm:inline">New</span>
                        <span className="hidden sm:inline ml-1">Event</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setLocation(`/analytics/${groupedEvents[selectedOrganization].org.id}`)}
                        className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 hidden sm:flex"
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Analytics
                      </Button>
                    </>
                  )}
                  
                  {/* Utility buttons */}
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowHelpModal(true)}
                    className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                  
                  {isPWA && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleForceRefresh}
                      disabled={isRefreshing}
                      className="bg-green-50/40 backdrop-blur-md hover:bg-green-100/50 border-green-200/60 text-green-700"
                    >
                      <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                  
                  {!isPWA && isMobile && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowPWAInstallModal(true)}
                      className="bg-blue-50/40 backdrop-blur-md hover:bg-blue-100/50 border-blue-200/60 text-blue-700"
                    >
                      <Smartphone className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Second row - Tab controls for time and view */}
              <div className="flex flex-col gap-3">
                {/* Mobile: All controls on one row, Desktop: Two groups */}
                <div className="flex flex-row gap-2 sm:gap-4 justify-between items-center">
                  {/* Time filter tabs */}
                  <Tabs value={showOnlyUpcoming ? "upcoming" : "past"} onValueChange={(value) => setShowOnlyUpcoming(value === "upcoming")}>
                    <TabsList className="bg-white/40 backdrop-blur-md border border-white/30">
                      <TabsTrigger value="upcoming" className="data-[state=active]:bg-blue-600/80 data-[state=active]:text-white">
                        <Calendar className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Upcoming</span>
                      </TabsTrigger>
                      <TabsTrigger value="past" className="data-[state=active]:bg-blue-600/80 data-[state=active]:text-white">
                        <CheckCircle className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Past</span>
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  {/* View mode tabs - always visible */}
                  <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'grid' | 'list')}>
                    <TabsList className="bg-white/40 backdrop-blur-md border border-white/30">
                      <TabsTrigger value="grid" className="data-[state=active]:bg-blue-600/80 data-[state=active]:text-white">
                        <Grid3x3 className="h-4 w-4" />
                      </TabsTrigger>
                      <TabsTrigger value="list" className="data-[state=active]:bg-blue-600/80 data-[state=active]:text-white">
                        <List className="h-4 w-4" />
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                
                {/* Event count badge - separate row on mobile, inline on desktop */}
                <div className="flex justify-center sm:justify-start">
                  <Badge variant="secondary" className="bg-white/50 backdrop-blur-sm">
                    {filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Display */}
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 bg-white/20 backdrop-blur-xl rounded-2xl border border-white/20 shadow-lg"
            style={{
              boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}>
            <Music className="h-16 w-16 mx-auto mb-4 text-slate-400 drop-shadow-sm" />
            <h3 className="text-lg font-medium text-slate-900 mb-2 drop-shadow-sm">
              No events found
            </h3>
            <p className="text-slate-600 drop-shadow-sm">
              {globalSearchTerm ? 
                "Try adjusting your search terms or filters" : 
                selectedOrganization === 'all' ?
                  "You are not part of any organisations with events" :
                  "No events match your current filters"}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvents.map(event => (
              <EventCard key={event.id} event={event} onSelect={handleSelectEvent} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map(event => {
              const canManage = userIsOwnerOfOrg(event.organisationId);
              return (
                <Card 
                  key={event.id}
                  onClick={() => handleSelectEvent(event.id)}
                  className="cursor-pointer hover:scale-[1.01] transition-all duration-300 bg-white/20 backdrop-blur-xl border-white/20 shadow-lg"
                  style={{
                    background: event.completed
                      ? 'linear-gradient(135deg, rgba(156,163,175,0.3) 0%, rgba(156,163,175,0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                  }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-4 flex-1">
                        {event.organisation?.logoUrl && (
                          <Squircle 
                            src={event.organisation.logoUrl} 
                            alt="Organization Logo" 
                            size={40}
                            className="flex-shrink-0 hidden sm:block"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-slate-900 drop-shadow-sm truncate">{event.name}</h3>
                            <Badge variant={event.type === "jam" ? "default" : event.type === "openmic" ? "secondary" : "outline"} 
                              className="text-xs">
                              {event.type === "jam" ? "Jam" : event.type === "openmic" ? "Open Mic" : "Event"}
                            </Badge>
                            {event.completed && (
                              <Badge variant="outline" className="text-xs bg-gray-100/50">
                                Completed
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(event.date), "MMM d, yyyy 'at' h:mm a")}
                            </span>
                            {event.venue && (
                              <span className="flex items-center gap-1 truncate">
                                <MapPin className="h-3 w-3" />
                                {event.venue}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {event.performerCount}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canManage && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <EventActionsDropdown
                              jamNight={event}
                              canManageEvent={true}
                              variant={isMobile ? "icon" : "button"}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        </main>

        {/* Event Management Modals now handled by EventActionsDropdown component */}

        {typeof showCreateModal === 'number' && showCreateModal > 0 && (
          <CreateEventModal
            isOpen={true}
            onOpenChange={() => setShowCreateModal(false)}
            organisationId={showCreateModal}
          />
        )}

        <HelpModal isOpen={showHelpModal} onOpenChange={setShowHelpModal} />
        <PWAInstallModal isOpen={showPWAInstallModal} onOpenChange={setShowPWAInstallModal} />
    </PageBackground>
  );
}
